import React, { useEffect } from "react";
import { Box, Button } from "@material-ui/core";
import { CTextField, CSelect, CAutocomplete, HFlex, ProgressButton, Text, useErrorContext } from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Save } from "@material-ui/icons";
import { useStates } from "shared/ValueTable";
import {
  useCreateHealthCareOrganization,
  useUpdateHealthCareOrganization,
  useCountries,
  HealthCareOrganization,
} from "../queries/useHealthCareOrganizations";

interface Props {
  organization?: HealthCareOrganization;
  onSuccess: (created?: HealthCareOrganization) => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  contact_email: string;
  billing_email: string;
  iana_timezone: string;
  organization_type: string;
  platform_fee: string;
  platform_fee_unit: string;
  country_id: number | null;
  us_state_id: number | null;
}

const timezoneOptions = [
  { value: "", label: "None" },
  { value: "America/New_York", label: "America/New_York" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Denver", label: "America/Denver" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles" },
  { value: "America/Phoenix", label: "America/Phoenix" },
  { value: "America/Anchorage", label: "America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
];

const organizationTypeOptions = [
  { value: "", label: "None" },
  { value: "sme", label: "SME" },
  { value: "enterprise", label: "Enterprise" },
];

const platformFeeUnitOptions = [
  { value: "", label: "None" },
  { value: "cents", label: "Cents" },
  { value: "percent", label: "Percent" },
];

export const HealthCareOrganizationForm: React.VFC<Props> = ({ organization, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const states = useStates();
  const countriesQuery = useCountries();
  const countries = countriesQuery.data ?? [];
  const usCountry = countries.find(c => c.country_code === "US");

  const form = useForm<FormData>({
    defaultValues: {
      name: organization?.name || "",
      contact_email: organization?.contact_email || "",
      billing_email: organization?.billing_email || "",
      iana_timezone: organization?.iana_timezone || "",
      organization_type: organization?.organization_type || "",
      platform_fee: organization?.platform_fee != null ? String(organization.platform_fee) : "",
      platform_fee_unit: organization?.platform_fee_unit || "",
      country_id: organization?.country_id ?? usCountry?.id ?? null,
      us_state_id: organization?.us_state_id ?? null,
    },
  });

  useEffect(() => {
    if (usCountry && !form.getValues("country_id")) {
      form.setValue("country_id", usCountry.id);
    }
  }, [usCountry, form]);

  const stateOptions = (states.data ?? []).map(s => ({ value: s.id as number, label: `${s.value} (${s.code})` }));

  const createMutation = useCreateHealthCareOrganization();
  const updateMutation = useUpdateHealthCareOrganization();

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const isEditing = !!organization;

  const submit = form.handleSubmit(async fields => {
    try {
      const data = {
        name: fields.name,
        contact_email: fields.contact_email || undefined,
        billing_email: fields.billing_email || undefined,
        iana_timezone: fields.iana_timezone || undefined,
        organization_type: fields.organization_type || undefined,
        platform_fee: fields.platform_fee ? Number(fields.platform_fee) : undefined,
        platform_fee_unit: fields.platform_fee_unit || undefined,
        country_id: fields.country_id ?? usCountry?.id ?? undefined,
        us_state_id: fields.us_state_id ?? undefined,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({ id: organization.id, ...data });
        onSuccess();
      } else {
        const created = await createMutation.mutateAsync(data);
        onSuccess(created);
      }
    } catch (err) {
      setError(
        isEditing
          ? t("errors.could_not_update_health_care_organization")
          : t("errors.could_not_create_health_care_organization"),
        err
      );
    }
  });

  return (
    <Box mx={3}>
      <Text heading bold mb={3}>
        {isEditing ? t("form.edit_health_care_organization") : t("form.create_health_care_organization")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField required name="name" autoFocus label={t("form.name")} margin="normal" size="small" fullWidth />

          <CTextField
            name="contact_email"
            label={t("form.hco_contact_email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />

          <CTextField
            name="billing_email"
            label={t("form.hco_billing_email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />

          <CSelect
            name="iana_timezone"
            label={t("settings.profile_information.time_zone")}
            items={timezoneOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CSelect
            name="organization_type"
            label={t("form.hco_organization_type")}
            items={organizationTypeOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CTextField
            name="platform_fee"
            label={t("form.hco_platform_fee")}
            type="number"
            margin="normal"
            size="small"
            fullWidth
          />

          <CSelect
            name="platform_fee_unit"
            label={t("form.hco_platform_fee_unit")}
            items={platformFeeUnitOptions}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CSelect
            name="country_id"
            label={t("form.hco_country")}
            items={countries.map(c => ({ value: c.id, label: c.name }))}
            margin="normal"
            size="small"
            fullWidth
            disabled
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CAutocomplete
            name="us_state_id"
            label={t("form.hco_state")}
            options={stateOptions}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
            margin="normal"
          />

          <HFlex justifyContent="flex-end" gap={2} my={4}>
            <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
              {t("buttons.cancel")}
            </Button>
            <ProgressButton loading={isLoading} endIcon={<Save />}>
              {isEditing ? t("buttons.save") : t("buttons.create")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};
